import type { ActionFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { authAPI, APIError } from "../../lib/api";
import {
  loginSchema,
  registerSchema,
  forgotPasswordSchema,
  resetPasswordSchema,
} from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";

export interface ActionResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  fieldErrors?: Record<string, string>;
}

export async function loginAction({
  request,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const formData = await parseFormData(request);
  const result = loginSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const response = await authAPI.login(result.data);
    return { success: true, data: response };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function registerAction({
  request,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const formData = await parseFormData(request);
  const result = registerSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const url = new URL(request.url);
    const bypass = url.searchParams.get("bypass") || undefined;
    await authAPI.register(
      {
        email: result.data.email,
        password: result.data.password,
        organizationName: result.data.organizationName || undefined,
      },
      bypass,
    );
    return redirect("/verify-email?registered=true");
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function forgotPasswordAction({ request }: ActionFunctionArgs): Promise<ActionResult> {
  const formData = await parseFormData(request);
  const result = forgotPasswordSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    await authAPI.forgotPassword(result.data);
    return { success: true };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function resetPasswordAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { token } = params;

  if (!token) {
    return { success: false, error: "Reset token is required" };
  }

  const formData = await parseFormData(request);
  const dataWithToken = { ...formData, token };
  const result = resetPasswordSchema.safeParse(dataWithToken);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    await authAPI.resetPassword({
      token,
      password: result.data.password,
    });
    return redirect("/login?reset=true");
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export interface ResendVerificationResult {
  success: boolean;
  error?: string;
  redirect?: string;
}

export async function resendVerificationAction(): Promise<ResendVerificationResult> {
  try {
    await authAPI.resendVerification();
    return { success: true };
  } catch (error) {
    if (error instanceof APIError) {
      if (error.statusCode === 401) {
        return {
          success: false,
          error: "Please log in first to resend verification email",
          redirect: "/login",
        };
      }
      if (error.statusCode === 429) {
        return {
          success: false,
          error: "Too many requests. Please try again later.",
        };
      }
      return { success: false, error: error.message || "Failed to resend verification email" };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
